import React, { useContext, useEffect, useState } from 'react';
import { Button, Form, Icon, Message, Modal } from 'semantic-ui-react';
import { OperationsContext } from '../OperationsContext';
import { useForm } from '../../../Hooks/useForm';
import { maskCurrency, unmaskCurrency, floatKeyDown, stateOptions } from '../../../Services/inputMasks';
import PercentageInput from '../../Shared/PercentageInput';

const SUTARatesModal = () => {
	// context
	const {
		operation,
		createRate,
		updateRate,
		unavailableStates,
		sutaRatesModal,
		hideSutaRatesModal,
		rateType,
		year
	} = useContext( OperationsContext );

	// form handler
	const { formData, updateField, setData } = useForm();

	// state
	const [ validator, setValidator ] = useState( {
		state: 'string',
		rate: 0,
		value: '0'
	} );

	// set formData to operation data
	useEffect( () => {
		if ( operation ) {
			const rateData = {
				state: operation.state,
				rate: operation.rate,
				value: maskCurrency( operation.value ),
				isVariable: operation.isVariable
			};

			setData( rateData );
		}

		return () => {
			setData( {} );
		};
	}, [ operation, setData ] );

	// prepare data for updateLead api call
	const handleUpdate = () => {
		updateRate( {
			...operation,
			state: formData.state,
			rate: formData.rate,
			value: unmaskCurrency( formData.value ),
			isVariable: formData.isVariable
		} );
	};

	// prepare data for updateLead api call
	const handleCreateNew = () => {
		createRate( {
			type: { id: rateType },
			year,
			state: formData.state,
			rate: formData.rate,
			value: unmaskCurrency( formData.value ),
			isVariable: formData.isVariable
		} );
	};

	// clear out formData, and validator when modal closes
	const clearData = () => {
		setData( {} );
		setValidator( {
			state: 'string',
			rate: 0,
			value: '0'
		} );
	};

	const filteredStateOptions = () => {
		if ( operation ) {
			return stateOptions.filter( option => option.value === operation.state || !unavailableStates.includes( option.value ) );
		}

		return stateOptions.filter( option => !unavailableStates.includes( option.value ) );
	};

	return (
		<Modal
			open={ sutaRatesModal }
			size="mini"
		>
			<Modal.Header>
        Administration Rate
			</Modal.Header>
			<Modal.Content>
				<Form>
					<Form.Select
						required
						name="state"
						value={ formData.state || '' }
						label="State"
						options={ filteredStateOptions() }
						width={ 3 }
						fluid
						search
						onChange={ ( e, { name, value } ) => updateField( { target: { name, value } } ) }
						error={ validator.state.length === 0 }
						onBlur={ () => {
							setValidator( { ...validator, state: formData.state || '' } );
						}}
						onFocus={ () => {
							setValidator( { ...validator, state: 'XX' } );
						}}
					/>
					<Message
						hidden={ validator.state.length > 0 }
						negative
						style={{ border: 'none', backgroundColor: 'initial', boxShadow: 'none', padding: 0 }}
					>
						<Message.List style={{ listStyle: 'none' }}>
							<Message.Item>
								<Icon name="exclamation circle" />
                Please select a state.
							</Message.Item>
						</Message.List>
					</Message>
					<PercentageInput
						required
						name="rate"
						label="Rate"
						value={ parseInt( formData.rate ) >= 0 ? formData.rate : '' }
						onChange={ updateField }
						error={ !( parseInt( validator.rate ) >= 0 ) }
						onBlur={ () => setValidator( { ...validator, rate: parseInt( formData.rate ) >= 0 ? formData.rate : '' } ) }
						onFocus={ () => setValidator( { ...validator, rate: 0 } ) }
					/>
					<Message
						hidden={
							parseInt( validator.rate ) >= 0
						}
						negative
						style={{ border: 'none', backgroundColor: 'initial', boxShadow: 'none', padding: 0 }}
					>
						<Message.List style={{ listStyle: 'none' }}>
							<Message.Item>
								<Icon name="exclamation circle" />
                Please enter a rate.
							</Message.Item>
						</Message.List>
					</Message>
					<Form.Input
						required
						name="value"
						label="Cutoff"
						value={ formData.value || '' }
						onChange={ updateField }
						error={ validator.value.length === 0 }
						onBlur={ e => {
							updateField( { target: { name: e.target.name, value: maskCurrency( e.target.value ) || '' } } );
							setValidator( { ...validator, value: formData.value || '' } );
						}}
						onFocus={ () => {
							setValidator( { ...validator, value: '0' } );
						}}
						onKeyDown={ floatKeyDown }
					>
					</Form.Input>
					<Message
						hidden={ validator.value.length > 0 }
						negative
						style={{ border: 'none', backgroundColor: 'initial', boxShadow: 'none', padding: 0 }}
					>
						<Message.List style={{ listStyle: 'none' }}>
							<Message.Item>
								<Icon name="exclamation circle" />
                Please enter a cutoff value.
							</Message.Item>
						</Message.List>
					</Message>
					<Form.Field>
						<label>Is Variable?</label>
						<Form.Checkbox
							name="isVariable"
							style={{ marginTop: '0.5rem' }}
							checked={ formData.isVariable || false }
							onChange={ ( e, { name, checked } ) => updateField( { target: { name, value: checked } } ) }
						/>
					</Form.Field>
				</Form>
			</Modal.Content>
			<Modal.Actions>
				<Button
					primary
					content="Save"
					onClick={ operation
						? () => {
							handleUpdate();
							clearData();
							hideSutaRatesModal();
						}
						: () => {
							handleCreateNew();
							clearData();
							hideSutaRatesModal();
						}
					}
					disabled={
						( formData.state ? formData.state.length === 0 : true )
            || !( parseInt( formData.rate ) >= 0 )
            || ( formData.value ? formData.value.length === 0 : true )
					}
				>
				</Button>
				<Button
					style={{ backgroundColor: '#F58229', color: '#fff' }}
					content="Cancel"
					onClick={ () => {
						clearData();
						hideSutaRatesModal();
					}}
				>
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

export default SUTARatesModal;
